import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { AvatarImage } from "@/components/ui/AvatarImage";
import { DisplayName } from "@/components/ui/DisplayName";
import { LoadingIndicator } from "@/components/ui/LoadingIndicator";
import { useAuth } from "@/context/AuthContext";
import { useSenderPhotoURL } from "@/hooks/useSenderPhotoURL";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useFormatTimeAgo } from "@/utils/formatDate";
import firestore, { Timestamp } from "@react-native-firebase/firestore";
import { router } from "expo-router";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { GestureResponderEvent } from "react-native";
import { FlatList, StyleSheet, TouchableOpacity } from "react-native";

type Notification = {
  id: string;
  type:
    | "LIKE"
    | "COMMENT"
    | "COMMENT_ON_FOLLOWED_POST"
    | "FOLLOW"
    | "NEW_POST"
    | "GROUP_INVITATION"
    | "GROUP_POST"
    | "GROUP_ACCESS_REQUEST"
    | "GROUP_ACCESS_APPROVED";
  sender: {
    uid: string;
    displayName?: string;
    photoURL?: string;
  };
  resourceId: string;
  groupId?: string;
  groupName?: string;
  createdAt: Timestamp;
  read: boolean;
};

const NotificationItem = memo(function NotificationItem({
  uid,
  item,
  isHighlighted,
  clearHighlight,
}: {
  uid?: string;
  item: Notification;
  isHighlighted: boolean;
  clearHighlight: (id: string) => void;
}) {
  const { formatTimeAgo } = useFormatTimeAgo();
  const backgroundColor = useThemeColor(
    {},
    isHighlighted ? "notificationBackground" : "background"
  );
  const textColor = useThemeColor({}, "text");
  const { t } = useTranslation();
  const resolvedPhotoURL = useSenderPhotoURL(item.sender?.uid);
  const [requestStatus, setRequestStatus] = useState<
    "pending" | "approved" | "declined" | "loading" | "unknown"
  >(item.type === "GROUP_ACCESS_REQUEST" ? "loading" : "unknown");
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    if (item.type !== "GROUP_ACCESS_REQUEST" || !item.resourceId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await firestore()
          .collection("groupAccessRequests")
          .doc(item.resourceId)
          .get();
        if (cancelled) return;
        const status = snap.exists() ? (snap.data()?.status as string) : null;
        if (status === "pending") setRequestStatus("pending");
        else if (status === "approved") setRequestStatus("approved");
        else if (status === "declined") setRequestStatus("declined");
        else setRequestStatus("unknown");
      } catch {
        if (!cancelled) setRequestStatus("unknown");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.resourceId, item.type]);

  const markRead = async () => {
    if (!uid || item.read) return;
    try {
      await firestore()
        .collection("users")
        .doc(uid)
        .collection("notifications")
        .doc(item.id)
        .update({ read: true });
    } catch {
      // best-effort
    }
  };

  const handleApprove = async () => {
    if (!item.groupId || !item.resourceId || !item.sender?.uid) return;
    clearHighlight(item.id);
    setActionPending(true);
    try {
      const batch = firestore().batch();
      batch.update(firestore().collection("groups").doc(item.groupId), {
        memberIds: firestore.FieldValue.arrayUnion(item.sender.uid),
      });
      batch.update(
        firestore().collection("groupAccessRequests").doc(item.resourceId),
        { status: "approved" },
      );
      await batch.commit();
      setRequestStatus("approved");
      await markRead();
    } catch (error) {
      console.error("Error approving from notification:", error);
    } finally {
      setActionPending(false);
    }
  };

  const handleDecline = async () => {
    if (!item.resourceId) return;
    clearHighlight(item.id);
    setActionPending(true);
    try {
      await firestore()
        .collection("groupAccessRequests")
        .doc(item.resourceId)
        .update({ status: "declined" });
      setRequestStatus("declined");
      await markRead();
    } catch (error) {
      console.error("Error declining from notification:", error);
    } finally {
      setActionPending(false);
    }
  };

  const goToSenderProfile = (e?: GestureResponderEvent) => {
    e?.stopPropagation?.();
    if (!item.sender?.uid) return;
    router.push({
      pathname: "/(tabs)/public-profile",
      params: { userId: item.sender.uid },
    });
  };

  const handlePress = () => {
    if (!uid) return;
    clearHighlight(item.id);
    void markRead();

    if (
      item.type === "COMMENT" ||
      item.type === "COMMENT_ON_FOLLOWED_POST" ||
      item.type === "LIKE" ||
      item.type === "NEW_POST" ||
      item.type === "GROUP_POST"
    ) {
      router.push({
        pathname: "/(tabs)/post/[postId]",
        params: { postId: item.resourceId },
      });
    } else if (item.type === "GROUP_INVITATION") {
      router.push({ pathname: "/(tabs)/groups" });
    } else if (item.type === "GROUP_ACCESS_REQUEST") {
      if (item.groupId) {
        router.push({
          pathname: "/(tabs)/groups/group-info",
          params: { groupId: item.groupId },
        });
      } else {
        router.push({ pathname: "/(tabs)/groups" });
      }
    } else if (item.type === "GROUP_ACCESS_APPROVED") {
      if (item.groupId) {
        router.push({
          pathname: "/(tabs)/groups/[id]",
          params: { id: item.groupId },
        });
      } else {
        router.push({ pathname: "/(tabs)/groups" });
      }
    }
  };

  const timestampLabel = useMemo(
    () => formatTimeAgo(item.createdAt.toDate().toISOString()),
    [item.createdAt, formatTimeAgo],
  );

  const notificationText = () => {
    switch (item.type) {
      case "LIKE":
        return t("LIKED_YOUR_POST");
      case "COMMENT":
        return t("COMMENTED_ON_YOUR_POST");
      case "COMMENT_ON_FOLLOWED_POST":
        return t("COMMENTED_ON_POST_YOU_COMMENTED_ON");
      case "FOLLOW":
        return t("STARTED_FOLLOWING_YOU");
      case "NEW_POST":
        return t("CREATED_A_NEW_POST");
      case "GROUP_INVITATION":
        return t("INVITED_YOU_TO_GROUP");
      case "GROUP_POST":
        return t("POSTED_IN_GROUP", { groupName: item.groupName ?? "" });
      case "GROUP_ACCESS_REQUEST":
        return t("REQUESTED_GROUP_ACCESS", { groupName: item.groupName ?? "" });
      case "GROUP_ACCESS_APPROVED":
        return t("GROUP_ACCESS_APPROVED_BODY", {
          groupName: item.groupName ?? "",
        });
      default:
        return t("PUBLISHED_A_NOTIFICATION");
    }
  };

  return (
    <TouchableOpacity onPress={handlePress}>
      <ThemedView style={[styles.notificationContainer, { backgroundColor }]}>
        <AvatarImage
          photoURL={resolvedPhotoURL}
          alt={item.sender?.displayName ?? "Avatar"}
          style={styles.avatar}
          userId={item.sender?.uid}
        />
        <ThemedView style={styles.textContainer}>
          <ThemedText>
            <ThemedText
              onPress={goToSenderProfile}
              type="defaultSemiBold"
              style={{ color: textColor }}
            >
              <DisplayName
                user={item.sender}
                type="defaultSemiBold"
                style={{ color: textColor }}
              />
            </ThemedText>{" "}
            <ThemedText style={{ color: textColor }}>
              {notificationText()}
            </ThemedText>
          </ThemedText>
          <ThemedText style={[styles.timestamp, { color: textColor }]}>
            {timestampLabel}
          </ThemedText>
          {item.type === "GROUP_ACCESS_REQUEST" &&
            requestStatus === "pending" && (
              <ThemedView style={styles.actions}>
                <TouchableOpacity
                  onPress={handleApprove}
                  disabled={actionPending}
                  style={styles.approveButton}
                >
                  <ThemedText style={styles.actionButtonText}>
                    {t("APPROVE")}
                  </ThemedText>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={handleDecline}
                  disabled={actionPending}
                  style={styles.declineButton}
                >
                  <ThemedText style={styles.actionButtonText}>
                    {t("DECLINE")}
                  </ThemedText>
                </TouchableOpacity>
              </ThemedView>
            )}
          {item.type === "GROUP_ACCESS_REQUEST" &&
            requestStatus === "approved" && (
              <ThemedText style={[styles.statusLabel, { color: "#16a34a" }]}>
                {t("APPROVED")}
              </ThemedText>
            )}
          {item.type === "GROUP_ACCESS_REQUEST" &&
            requestStatus === "declined" && (
              <ThemedText style={[styles.statusLabel, { color: "#9ca3af" }]}>
                {t("DECLINED")}
              </ThemedText>
            )}
        </ThemedView>
      </ThemedView>
    </TouchableOpacity>
  );
});

export default function NotificationsScreen() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const wasUnreadIdsRef = useRef<Set<string> | null>(null);
  const [visitUnreadIds, setVisitUnreadIds] = useState<Set<string>>(
    () => new Set()
  );
  const clearHighlight = useCallback((id: string) => {
    setVisitUnreadIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const { t } = useTranslation();

  useEffect(() => {
    if (!user) return;

    const q = firestore()
      .collection("users")
      .doc(user.uid)
      .collection("notifications")
      .orderBy("createdAt", "desc");

    const unsubscribe = q.onSnapshot(
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Notification[];
        setNotifications(notifs);
        setLoading(false);
      },
      (err) => {
        console.error("[notifications] snapshot error", err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid || wasUnreadIdsRef.current !== null) return;
    if (notifications.length === 0) return;
    const unread = notifications.filter((n) => !n.read).map((n) => n.id);
    wasUnreadIdsRef.current = new Set(unread);
    if (unread.length === 0) return;
    setVisitUnreadIds(new Set(unread));
    // Firestore batches cap at 500 writes; not a concern at current scale.
    const batch = firestore().batch();
    unread.forEach((id) =>
      batch.update(
        firestore()
          .collection("users")
          .doc(uid)
          .collection("notifications")
          .doc(id),
        { read: true },
      ),
    );
    batch.commit().catch(() => {
      /* best-effort — matches per-row markRead */
    });
  }, [notifications, user?.uid]);

  const renderItem = useCallback(
    ({ item }: { item: Notification }) => (
      <NotificationItem
        uid={user?.uid}
        item={item}
        isHighlighted={visitUnreadIds.has(item.id)}
        clearHighlight={clearHighlight}
      />
    ),
    [user?.uid, visitUnreadIds, clearHighlight],
  );

  if (loading) {
    return (
      <ThemedView style={styles.container}>
        <LoadingIndicator />
      </ThemedView>
    );
  }

  return (
    <ThemedView style={styles.container}>
      {notifications.length > 0 ? (
        <FlatList
          data={notifications}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          extraData={visitUnreadIds}
        />
      ) : (
        <ThemedText style={styles.noNotificationsText}>
          {t("NO_NOTIFICATIONS")}
        </ThemedText>
      )}
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
  },
  notificationContainer: {
    flexDirection: "row",
    padding: 15,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 15,
  },
  textContainer: {
    flex: 1,
    padding: 5,
  },
  timestamp: {
    fontSize: 12,
    marginTop: 4,
  },
  noNotificationsText: {
    textAlign: "center",
  },
  actions: {
    flexDirection: "row",
    marginTop: 8,
    gap: 8,
  },
  approveButton: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    backgroundColor: "#16a34a",
    borderRadius: 6,
  },
  declineButton: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    backgroundColor: "#9ca3af",
    borderRadius: 6,
  },
  actionButtonText: {
    color: "white",
    fontSize: 13,
  },
  statusLabel: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: "600",
  },
});